import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/constants";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, CheckCircle2, Circle, Trash2, ListChecks, CalendarClock } from "lucide-react";

const MANAGER_ROLES = ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"];

export default function Tasks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [scope, setScope] = useState("mine"); // mine | team
  const [statusFilter, setStatusFilter] = useState("all"); // all | open | done
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (isManager && scope === "team") params.scope = "team";
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/tasks", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [isManager, scope, statusFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isManager) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [isManager]);

  const toggleDone = async (t) => {
    try {
      await api.put(`/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (t) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Task deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const userName = (uid) => users.find((u) => u.id === uid)?.name || "—";

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {isManager && (
            <div className="inline-flex rounded-lg border border-navy/10 bg-white p-0.5">
              <button onClick={() => setScope("mine")} className={`rounded-md px-3 py-1.5 text-sm font-medium ${scope === "mine" ? "bg-navy text-white" : "text-muted-foreground hover:text-ink"}`} data-testid="tasks-scope-mine">My Tasks</button>
              <button onClick={() => setScope("team")} className={`rounded-md px-3 py-1.5 text-sm font-medium ${scope === "team" ? "bg-navy text-white" : "text-muted-foreground hover:text-ink"}`} data-testid="tasks-scope-team">Team Tasks</button>
            </div>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-32 bg-white" data-testid="tasks-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="done">Done</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-navy text-white hover:bg-navy-hover" data-testid="new-task-btn">
          <Plus className="mr-2 h-4 w-4" /> New Task
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-16 text-muted-foreground">
            <ListChecks className="h-8 w-8 opacity-40" />
            <p>No tasks</p>
          </div>
        ) : (
          <div className="divide-y divide-navy/5">
            {items.map((t) => {
              const done = t.status === "done";
              return (
                <div key={t.id} className="flex items-start gap-3 px-4 py-3 hover:bg-nude/30" data-testid={`task-${t.id}`}>
                  <button onClick={() => toggleDone(t)} className="mt-0.5 shrink-0" title={done ? "Mark open" : "Mark done"} data-testid={`task-toggle-${t.id}`}>
                    {done ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <Circle className="h-5 w-5 text-muted-foreground hover:text-navy" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={`font-medium ${done ? "text-muted-foreground line-through" : "text-ink"}`}>{t.title}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      <span className={`flex items-center gap-1 ${t.overdue ? "font-semibold text-red-600" : ""}`}>
                        <CalendarClock className="h-3.5 w-3.5" /> {formatDateTime(t.due_at)}{t.overdue ? " · Overdue" : ""}
                      </span>
                      {t.lead_name && (
                        <button onClick={() => navigate(`/leads/${t.lead_id}`)} className="text-navy hover:underline">{t.lead_name}</button>
                      )}
                      {isManager && scope === "team" && <span>· {userName(t.assigned_to)}</span>}
                    </div>
                    {t.notes && <p className="mt-1 text-xs text-muted-foreground">{t.notes}</p>}
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => remove(t)} data-testid={`task-delete-${t.id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CreateTask open={open} onOpenChange={setOpen} isManager={isManager} users={users} onDone={load} />
    </div>
  );
}

function CreateTask({ open, onOpenChange, isManager, users, onDone }) {
  const empty = { title: "", due_at: "", assigned_to: "", notes: "" };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title || !form.due_at) { toast.error("Title and due date required"); return; }
    try {
      await api.post("/tasks", {
        title: form.title,
        due_at: new Date(form.due_at).toISOString(),
        assigned_to: form.assigned_to || null,
        notes: form.notes,
      });
      toast.success("Task created");
      setForm(empty); onOpenChange(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-navy">New Task</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Title *"><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="task-title" /></Field>
          <Field label="Due Date & Time *"><Input type="datetime-local" value={form.due_at} onChange={(e) => set("due_at", e.target.value)} data-testid="task-due" /></Field>
          {isManager && (
            <Field label="Assign To">
              <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
                <SelectTrigger><SelectValue placeholder="Myself" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-task-btn">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
